import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

class RecentlyViewedProducts extends Component {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.productsLoaded === 'true') return;

    if (this.#getViewedProductIds().length === 0) {
      if (!Shopify.designMode) {
        this.#hide();
      }
      return;
    }

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * @returns {string[]}
   */
  #getViewedProductIds() {
    const { productId, maxProducts } = this.dataset;
    const limit = Number(maxProducts) || 4;

    return RecentlyViewed.getProducts()
      .filter((id) => id !== productId)
      .slice(0, limit);
  }

  #loadProducts() {
    const { sectionId, productsLoaded } = this.dataset;

    if (productsLoaded === 'true' || !sectionId) return;

    const viewedProducts = this.#getViewedProductIds();

    if (viewedProducts.length === 0) {
      if (!Shopify.designMode) {
        this.#hide();
      }
      return;
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    sectionRenderer
      .getSectionHTML(sectionId, false, url, this.#activeFetch.signal)
      .then((html) => {
        if (this.#activeFetch?.signal.aborted) return;

        if (!html?.trim()) {
          if (!Shopify.designMode) {
            this.#hide();
          }
          return;
        }

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const section = parsed.getElementById(`shopify-section-${sectionId}`);

        if (section?.querySelector('[data-has-products="false"]')) {
          if (!Shopify.designMode) {
            this.#hide();
          }
          return;
        }

        if (!section?.querySelector('[data-has-products="true"]')) {
          if (!Shopify.designMode) {
            this.#hide();
          }
          return;
        }

        this.dataset.productsLoaded = 'true';
        morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
      })
      .catch((error) => {
        if (this.#activeFetch?.signal.aborted || error?.name === 'AbortError') return;
        console.error(error);
        if (!Shopify.designMode) {
          this.#hide();
        }
      })
      .finally(() => {
        this.#activeFetch = null;
      });
  }

  #hide() {
    this.dataset.productsLoaded = 'true';
    this.classList.add('hidden');
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
